import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { Observable, of } from 'rxjs';
import { PageBaseComponent } from '../../abstracts/page-base/page-base.component';
import { Organization } from '../../models/organization';
import { UserTeam } from '../../models/team';
import { ApiService } from '../../services/api.service';
import { Functions } from '../../utilities/functions';

@Component({
  selector: 'app-organizations',
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    DialogModule,
    TabsModule,
    ToggleSwitchModule,
    TextareaModule,
    SelectModule
  ],
  providers: [MessageService],
  templateUrl: './organizations.component.html',
  styles: ``
})
export class OrganizationsComponent extends PageBaseComponent<Organization> {
  organizations: Organization[] = [];

  selectedOrganizationTeams: UserTeam[] = [];

  constructor(apiService: ApiService, messageService: MessageService) {
    super(apiService, messageService)
  }

  override onCloseDialog(): void {
    this.showDialog = false;
  }

  onExportToCSV(dt: Table, includeTeams: boolean = false) {

    // Seleziono i dati filtrati -se è attivo un filtro, altrimenti tutti- e visibili (in tutte le pagine, non solo la prima)
    let filteredData = dt.filteredValue ?? dt.value;
    let columns = [
      { field: 'organizationId', header: 'ID' },
      { field: 'name', header: 'Organizzazione' },
      { field: 'isActive', header: 'Attiva' },
      { field: 'updatedAt', header: 'Ultimo aggiornamento' }
    ];

    if (includeTeams) {
      // Se voglio includere i dipartimenti, li carico dal server
      this.apiService.getTeams().subscribe({
        next: (data) => {
          const teams = data;

          const teamMap = new Map<number, string[]>();
          teams.forEach(t => {
            if (!teamMap.has(t.organizationId)) teamMap.set(t.organizationId, []);
            teamMap.get(t.organizationId)?.push(t.name);
          });

          // Creo proprietà virtuale departmentNames
          filteredData = filteredData.map(org => ({
            ...org,
            teamNames: teamMap.get(org.organizationId)?.join(', ') ?? ''
          }));

          columns.push({ field: 'teamNames', header: 'Squadre' });
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          this.showError(err);

          return;
        },
        complete: () => {
          this.loading = false;
        }
      })
    }

    Functions.downloadCSV('organizations', filteredData, columns);

    this.dtTrigger.next(true);
  }

  override onSave(): void {
    if (this.isNew) {
      this.apiService.addOrganization(this.selectedItem).subscribe({
        next: (r) => {
          if (r) {
            this.showDialog = false;
            this.showSuccess("L'organizzazione è stata salvata correttamente");
            this.resetItems();
          } else {
            this.showErrorMessage("Il salvataggio non è stato completato!")
          }
        },
        error: (err: HttpErrorResponse) => {
          this.showError(err);
        },
        complete: () => {
          this.loadData();
        }
      });
    } else {
      this.apiService.updateOrganization(this.selectedItem).subscribe({
        next: (r) => {
          if (r) {
            this.showDialog = false;
            this.showSuccess("L'organizzazione è stata aggiornata correttamente");
            this.resetItems();
          } else {
            this.showErrorMessage("L'aggiornamento non è stato completato!")
          }
        },
        error: (err: HttpErrorResponse) => {
          this.showError(err);
        },
        complete: () => {
          this.loadData();
        }
      });
    }
  }

  protected override loadData(): void {
    this.loading = true;

    this.apiService.getOrganizations().subscribe({
      next: (data) => {
        this.organizations = data;
        this.dtTrigger.next(true);
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.showError(err);
      },
      complete: () => {
        this.loading = false;
      }
    })
  }

  protected override loadSelectedInfo(): Observable<boolean> {
    this.isNew = this.selectedItem.organizationId === 0;
    this.selectedOrganizationTeams = [];

    if (!this.isNew) {
      this.apiService.getOrganizationTeams(this.selectedItem.organizationId).subscribe({
        next: (d) => {
          this.selectedOrganizationTeams = d;
        },
        error: (err: HttpErrorResponse) => {
          this.selectedOrganizationTeams = [];
          throw err;
        }
      });
    }

    return of(true);
  }

  protected override resetItems(): void {
    this.selectedItem = {
      organizationId: 0,
      name: '',
      description: '',
      isActive: false,
      updatedAt: new Date()
    };

    this.newItem = {
      organizationId: 0,
      name: '',
      description: '',
      isActive: false,
      updatedAt: new Date()
    };
  }
}
